// ============================================================================
// PORTAL CASE REPOSITORY
// ARQUIVO: src/services/PortalCaseRepository.js
// RESPONSABILIDADE: Operações Firestore do Portal do Caso (workspaces, docs, timeline).
// Criado: Fase 6.3 — mover Firestore direto de PortalCaso.jsx para repositório.
// ============================================================================

import {
  collection, doc, getDoc, onSnapshot, query, where, limit, orderBy, updateDoc, addDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';
import { FS } from '../shared/constants/firestorePaths';

/** Observa workspaces do cliente (dupla query: clientId + caseTeamIds). */
export function watchClientWorkspaces(appId, clientId, onData) {
  const col = collection(db, ...FS.autosWorkspaces(appId));
  const rowsByQuery = { byClient: [], byTeam: [] };
  const merge = () => {
    const seen = new Set();
    const merged = [];
    for (const row of [...rowsByQuery.byClient, ...rowsByQuery.byTeam]) {
      if (!seen.has(row.id)) { seen.add(row.id); merged.push(row); }
    }
    onData(merged);
  };

  const unsub1 = onSnapshot(
    query(col, where('clientId', '==', clientId)),
    (snap) => { rowsByQuery.byClient = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })); merge(); },
    () => { rowsByQuery.byClient = []; merge(); }
  );
  const unsub2 = onSnapshot(
    query(col, where('caseTeamIds', 'array-contains', clientId)),
    (snap) => { rowsByQuery.byTeam = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })); merge(); },
    () => { rowsByQuery.byTeam = []; merge(); }
  );
  return () => { unsub1(); unsub2(); };
}

/** Observa documentos formais do cliente (CONTRACT, PROCURATION, etc.). */
export function watchClientFormalDocuments(appId, clientId, onData) {
  const col = collection(db, ...FS.documents(appId));
  const unsub = onSnapshot(
    query(col, where('clientId', '==', clientId), limit(50)),
    (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((r) => {
          const t = String(r?.type || '').trim().toUpperCase();
          const c = String(r?.classification?.category || r?.category || '').toLowerCase();
          const isFormal = t === 'CONTRACT' || t === 'PROCURATION' || t === 'DECLARATION' || t === 'PETITION' || t === 'INFORMED_CONSENT' || t === 'MANDATE_TERMINATION'
            || c.includes('contrat') || c.includes('procura') || c.includes('declara') || c.includes('peti') || c.includes('consentimento') || c.includes('renunc') || c.includes('revoga');
          if (!isFormal) return false;
          if (r?.clientVisible === false) return false;
          const fmt = String(r?.publication?.format || r?.format || '').toLowerCase();
          return fmt === 'pdf' || fmt === 'html';
        });
      onData(docs);
    },
    () => onData([])
  );
  return () => { try { unsub?.(); } catch {} };
}

/** Atualiza documento com dados de assinatura. */
export async function updateDocumentSignature(appId, docId, data) {
  const docRef = doc(db, ...FS.documents(appId), docId);
  await updateDoc(docRef, {
    ...data,
    signedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Atualiza workspace com dados de assinatura do Drive. */
export async function updateWorkspaceSignedDrive(appId, wsId, topUpdate) {
  const wsDocRef = doc(db, ...FS.autosWorkspaces(appId), wsId);
  const wsSnap = await getDoc(wsDocRef);
  const wsData = wsSnap.exists() ? wsSnap.data() : null;
  const orgStates = wsData?.organizerStates || {};
  for (const pieceKey of Object.keys(orgStates)) {
    const slotId = Object.keys(topUpdate).find(k => k.includes('signedDriveId'));
    if (slotId && orgStates[pieceKey]?.assignments?.[slotId]) {
      // topUpdate já contém as chaves per-piece
    }
  }
  await updateDoc(wsDocRef, {
    ...topUpdate,
    updatedAt: serverTimestamp(),
  }).catch(() => null);
}

/** Adiciona nota de evento ao cliente. */
export async function addClientEventNote(appId, clientId, noteData) {
  const { addDoc: addDocFn } = await import('firebase/firestore');
  await addDocFn(
    collection(db, ...FS.clientNotes(appId, clientId)),
    {
      ...noteData,
      createdAt: serverTimestamp(),
    }
  ).catch(() => null);
}

/** Observa timeline de eventos do cliente. */
export function watchClientTimeline(appId, clientId, hiddenTypes, onData) {
  const colRef = appId
    ? collection(db, ...FS.clientNotes(appId, clientId))
    : collection(db, 'clients', clientId, 'notes');
  const unsub = onSnapshot(
    query(colRef, orderBy('createdAt', 'desc'), limit(100)),
    (snap) => onData(
      snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((ev) => !hiddenTypes.has(String(ev?.type || '').trim().toUpperCase()))
    ),
    () => onData([])
  );
  return () => { try { unsub?.(); } catch {} };
}
